import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Minimize } from "lucide-react";
import { useElementFullscreen } from "./wallFullscreen";
import FullScreenPlayerControls from "../../player/controls/FullScreenPlayerControls";
import type { ControlButton, PlayerControlsSeek, PlayerControlsSpec } from "../../player/controls/playerControlsSpec";
import { usePlayerControlsVisibility } from "../../player/controls/usePlayerControlsVisibility";
import type { CastFullScreen } from "./castControlsSpec";
import { useAutoplayBlocked } from "../../hooks/useAutoplayBlocked";
import { useMediaCastDriver } from "../../hooks/useMediaCastDriver";
import { useCastFollower, type KeyedDriver } from "../../hooks/useCastFollower";
import type { PlayerState } from "../../lib/castSync/castFollower";
import type { StreamSlot } from "../../types/stream";
import { VideoPlayer, videoState } from "../../player";

interface Props {
  slot: StreamSlot;
  /** Your own cast only: the player is stuck (useCastFollower `onStall`). */
  onStall?: (positionMs: number) => void;
  /** Every position report of the current media. */
  onPosition?: (positionMs: number, state: PlayerState) => void;
  /** Full screen: the card's controls (castControlsSpec). Without it: exit and the caster's name only. */
  fullScreen?: CastFullScreen;
}

export const FILE_REPORT_MS = 250;

/** The `<video>` element's state in the follower's terms (the player module's). */
export { videoState };

/**
 * A video or image cast. Video follows the server timeline through the cast follower:
 * scheduled start, seek on join, playback-rate nudging for small drift (spec 2026-10-12 cast sync).
 * The follower gets the driver only after the element has loaded the current src.
 */
export default function VmplFilePlayer({ slot, onStall, onPosition, fullScreen }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  // Our box around the video; fullscreen goes on it (VmplLinkPlayer's pattern). Its own document: the card may live
  // in a Document PiP window.
  const [frameEl, setFrameEl] = useState<HTMLDivElement | null>(null);
  const fs = useElementFullscreen(frameEl);
  const isFullscreen = fs.on;
  const url = slot.mediaType !== "IMAGE" && slot.mediaUrl ? slot.mediaUrl : null;
  const [driver, setDriver] = useState<KeyedDriver | null>(null);
  const loadedUrl = useRef<string | null>(null);
  const { castDriver, blockedRef } = useMediaCastDriver(videoRef);
  const { report, resync } = useCastFollower(slot, "file", driver, url, { onStall, onPosition });
  const { t } = useTranslation();
  const [playing, setPlaying] = useState(false);
  const [ended, setEnded] = useState(false);
  const bound = !!driver && driver.key === url;
  // The video's progress for the full-screen line (read each report).
  const [progress, setProgress] = useState({ pos: 0, dur: 0, buf: 0 });
  const vis = usePlayerControlsVisibility({ active: isFullscreen, itemKey: url ?? undefined });
  const { hold } = vis;
  const fsPaused = isFullscreen && slot.playStatus !== "PLAY";
  const fsSheet = isFullscreen && fullScreen?.dialog != null;
  useEffect(() => hold("paused", fsPaused), [hold, fsPaused]);
  useEffect(() => hold("sheet", fsSheet), [hold, fsSheet]);
  const onFsChange = useRef(fullScreen?.onChange);
  onFsChange.current = fullScreen?.onChange;
  useEffect(() => onFsChange.current?.(isFullscreen), [isFullscreen]);
  // Unmounted in full screen (another item type took the card): the card gets its own controls back.
  useEffect(() => () => onFsChange.current?.(false), []);
  const [blocked, setBlocked] = useAutoplayBlocked(
    slot,
    bound && slot.playStatus === "PLAY" && !playing && !ended,
    url,
  );

  function onLoaded() {
    if (!url || videoRef.current?.getAttribute("src") !== url) return;
    loadedUrl.current = url;
    setPlaying(false);
    setEnded(false);
    setDriver({ key: url, driver: castDriver });
  }

  blockedRef.current = () => setBlocked(true);

  useEffect(() => {
    const el = videoRef.current;
    if (!driver || !el) return;
    const id = setInterval(() => {
      if (!loadedUrl.current) return;
      const state = videoState(el);
      setPlaying(state === "playing");
      setEnded(state === "ended");
      const b = el.buffered;
      setProgress({
        pos: el.currentTime * 1000,
        dur: Number.isFinite(el.duration) ? el.duration * 1000 : 0,
        buf: b && b.length > 0 ? b.end(b.length - 1) * 1000 : 0,
      });
      report(loadedUrl.current, el.currentTime * 1000, state);
    }, FILE_REPORT_MS);
    return () => clearInterval(id);
  }, [driver, report]);

  if (!slot.mediaUrl) return null;

  const tapToListen = () => {
    setEnded(false);
    resync();
    setBlocked(false);
  };

  if (slot.mediaType === "IMAGE") {
    return (
      <div>
        {slot.streamingUser && (
          <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 4 }}>
            {slot.streamingUser.name}
          </div>
        )}
        <img src={slot.mediaUrl} alt="stream" style={{ width: "100%", borderRadius: 4 }} />
      </div>
    );
  }

  const exit: ControlButton = { testId: "dock-exit-fullscreen", label: t("exit_fullscreen"), icon: <Minimize aria-hidden="true" />, onClick: fs.exit, start: true };
  // The cast's server timeline drives the video: no seek from here (read-only line for host and viewer alike).
  const seek: PlayerControlsSeek = { positionMs: progress.pos, bufferedMs: progress.buf, durationMs: progress.dur, draggable: false, onSeek: () => {} };
  const fsSpec: PlayerControlsSpec = fullScreen
    ? fullScreen.spec({ exit, seek })
    : { title: slot.streamingUser?.name ?? "", top: [exit], middle: [], seek };

  return (
    <div
      ref={setFrameEl}
      data-testid="file-player-frame"
      className="group"
      style={{ position: "relative", ...(isFullscreen ? { background: "#000" } : null) }}
    >
      {slot.streamingUser && !isFullscreen && (
        <div
          style={{
            position: "absolute",
            bottom: 4,
            left: 6,
            color: "#fff",
            fontSize: 12,
            textShadow: "0 1px 3px #000",
            zIndex: 1,
          }}
        >
          {slot.streamingUser.name}
        </div>
      )}
      <FullScreenPlayerControls spec={fsSpec} visibility={vis} on={isFullscreen}>
        <VideoPlayer
          ref={videoRef}
          src={slot.mediaUrl}
          onLoadedMetadata={onLoaded}
          style={isFullscreen ? { width: "100%", height: "100%", objectFit: "contain" } : { width: "100%", aspectRatio: "16/9", borderRadius: 4 }}
          playsInline
        />
      </FullScreenPlayerControls>
      {/* The enter button; in full screen the shared top bar has the one exit. */}
      {!isFullscreen && (
      <button
        type="button"
        data-testid="btn-fullscreen"
        onClick={fs.toggle}
        title={t("fullscreen")}
        aria-label={t("fullscreen")}
        className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100 transition-opacity"
        style={{
          position: "absolute",
          top: 8,
          right: 8,
          zIndex: 1,
          width: 24,
          height: 24,
          borderRadius: 4,
          background: "rgba(0,0,0,0.5)",
          border: "none",
          color: "#fff",
          cursor: "pointer",
          fontSize: 12,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        ⛶
      </button>
      )}
      {isFullscreen && fullScreen?.dialog}
      {blocked && (
        <button
          data-testid="btn-tap-to-listen"
          onClick={tapToListen}
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.6)",
            border: "none",
            color: "#fff",
            cursor: "pointer",
            fontSize: 13,
          }}
        >
          ▶ {t("tap_to_listen", "Tap to listen")}
        </button>
      )}
    </div>
  );
}
